import React from 'react';
import { Info } from 'lucide-react';
import { dpsTheme } from '../../../styles/dpsTheme';
import { useDeviceSize } from '../../../hooks/useMediaQuery';

interface ActionProjectionBannerProps {
  currentScore: number;
  projectedScore: number;
  selectedActionsCount: number;
  estimatedCost: number;
}

export const ActionProjectionBanner: React.FC<ActionProjectionBannerProps> = ({
  currentScore,
  projectedScore,
  selectedActionsCount,
  estimatedCost
}) => {
  const { isMobile } = useDeviceSize();
  const scoreGain = projectedScore - currentScore;
  const hasSelection = selectedActionsCount > 0;

  if (!hasSelection) {
    return (
      <div
        style={{
          background: `linear-gradient(135deg, ${dpsTheme.colors.accent.blue.bg}, rgba(37, 99, 235, 0.1))`,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${dpsTheme.colors.accent.blue.border}`,
          borderRadius: dpsTheme.borderRadius.lg,
          padding: isMobile ? dpsTheme.spacing.md : dpsTheme.spacing.xl,
          marginBottom: dpsTheme.spacing['2xl'],
          textAlign: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: isMobile ? '8px' : '12px', flexWrap: 'wrap' }}>
          <svg width={isMobile ? '16' : '20'} height={isMobile ? '16' : '20'} viewBox="0 0 24 24" fill="none" stroke={dpsTheme.colors.text.muted} strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
          <span style={{ color: dpsTheme.colors.text.muted, fontSize: isMobile ? dpsTheme.typography.fontSize.sm : dpsTheme.typography.fontSize.base }}>
            Cochez des actions ci-dessous pour simuler l'évolution de votre score
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        background: `linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.1))`,
        backdropFilter: 'blur(20px)',
        border: `1px solid ${dpsTheme.colors.accent.green.border}`,
        borderRadius: dpsTheme.borderRadius.lg,
        padding: isMobile ? dpsTheme.spacing.md : dpsTheme.spacing.xl,
        marginBottom: dpsTheme.spacing['2xl'],
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', marginBottom: isMobile ? dpsTheme.spacing.sm : dpsTheme.spacing.md }}>
        <svg width={isMobile ? '16' : '20'} height={isMobile ? '16' : '20'} viewBox="0 0 24 24" fill="none" stroke={dpsTheme.colors.accent.green.primary} strokeWidth="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
        <h3 style={{ fontSize: isMobile ? dpsTheme.typography.fontSize.base : dpsTheme.typography.fontSize.lg, fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.primary, margin: 0 }}>
          Projection de votre score DPS
        </h3>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: isMobile ? dpsTheme.spacing.sm : dpsTheme.spacing.lg }}>
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: `1px solid ${dpsTheme.colors.border.secondary}`,
            borderRadius: dpsTheme.borderRadius.md,
            padding: isMobile ? dpsTheme.spacing.sm : dpsTheme.spacing.lg,
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: isMobile ? '9px' : dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted, marginBottom: isMobile ? '4px' : '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Score actuel
          </div>
          <div style={{ fontSize: isMobile ? dpsTheme.typography.fontSize.xl : dpsTheme.typography.fontSize['3xl'], fontWeight: dpsTheme.typography.fontWeight.bold, color: dpsTheme.colors.text.primary }}>
            {currentScore}
          </div>
        </div>

        <div
          style={{
            background: 'rgba(16, 185, 129, 0.1)',
            border: `1px solid ${dpsTheme.colors.accent.green.border}`,
            borderRadius: dpsTheme.borderRadius.md,
            padding: isMobile ? dpsTheme.spacing.sm : dpsTheme.spacing.lg,
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: isMobile ? '9px' : dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted, marginBottom: isMobile ? '4px' : '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Score projeté
          </div>
          <div style={{ fontSize: isMobile ? dpsTheme.typography.fontSize.xl : dpsTheme.typography.fontSize['3xl'], fontWeight: dpsTheme.typography.fontWeight.bold, color: dpsTheme.colors.accent.green.primary }}>
            {projectedScore}
          </div>
        </div>

        <div
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: `1px solid ${dpsTheme.colors.border.secondary}`,
            borderRadius: dpsTheme.borderRadius.md,
            padding: isMobile ? dpsTheme.spacing.sm : dpsTheme.spacing.lg,
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: isMobile ? '9px' : dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted, marginBottom: isMobile ? '4px' : '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Gain de points
          </div>
          <div style={{ fontSize: isMobile ? dpsTheme.typography.fontSize.xl : dpsTheme.typography.fontSize['3xl'], fontWeight: dpsTheme.typography.fontWeight.bold, color: dpsTheme.colors.accent.green.primary }}>
            +{scoreGain}
          </div>
        </div>

        <div
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: `1px solid ${dpsTheme.colors.border.secondary}`,
            borderRadius: dpsTheme.borderRadius.md,
            padding: isMobile ? dpsTheme.spacing.sm : dpsTheme.spacing.lg,
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: isMobile ? '9px' : dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted, marginBottom: isMobile ? '4px' : '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Coût estimé
          </div>
          <div style={{ fontSize: isMobile ? dpsTheme.typography.fontSize.lg : dpsTheme.typography.fontSize['2xl'], fontWeight: dpsTheme.typography.fontWeight.bold, color: dpsTheme.colors.text.primary }}>
            {estimatedCost.toLocaleString('fr-FR')} €
          </div>
        </div>
      </div>

      <div
        style={{
          marginTop: isMobile ? dpsTheme.spacing.sm : dpsTheme.spacing.lg,
          padding: isMobile ? dpsTheme.spacing.sm : dpsTheme.spacing.md,
          background: 'rgba(16, 185, 129, 0.1)',
          borderRadius: dpsTheme.borderRadius.sm,
          fontSize: isMobile ? dpsTheme.typography.fontSize.xs : dpsTheme.typography.fontSize.sm,
          color: dpsTheme.colors.text.secondary,
          textAlign: 'center',
        }}
      >
        <Info className={isMobile ? 'w-3 h-3 inline-block mr-1' : 'w-4 h-4 inline-block mr-1'} />  {selectedActionsCount} action{selectedActionsCount > 1 ? 's' : ''} sélectionnée{selectedActionsCount > 1 ? 's' : ''} pour simulation
      </div>
    </div>
  );
};